// Adresy stránek (krok B2): každá obrazovka má vlastní adresu, funguje
// tlačítko Zpět v prohlížeči, gesto zpět na iPhonu, obnovení stránky
// a odkazy přímo na audit nebo opatření.
//
// Princip: adresa se ODVOZUJE ze stavu aplikace (pathFromState) po každém
// render() — obrazovky dál přepínají jen state.view a detaily jako dosud.
// Změní-li se adresa, přidá se záznam do historie prohlížeče; vede-li změna
// na adresu předchozího záznamu (tlačítko „← Zpět“ v aplikaci), použije se
// history.back(), aby se historie prohlížeče a aplikace nerozešly.
// Při Zpět/Vpřed v prohlížeči (popstate) se stav nastaví z adresy
// (applyPath) a obrazovka se překreslí.
//
// Adresy (BASE = import.meta.env.BASE_URL, teď „/“):
//   /                         Přehled
//   /historie, /historie/kos, /historie/BOZP-12
//   /opatreni, /opatreni/OP-BOZP-12-1, /opatreni/audit/BOZP-12
//   /statistiky (plné statistiky na mobilu; Přehled je tam „Dnes“)
//   /plan
//   /audit (Rozpracované), /audit/zahajit (K zahájení), /audit/<id rozpracovaného auditu>
//   /nastaveni, /nastaveni/<podstránka> (viz SETTINGS_SLUGS),
//   /nastaveni/druhy-auditu/<id druhu>, /nastaveni/plany/<id plánu | novy>
// Audit a opatření jsou v adrese podle čísla (jedinečné v rámci firmy);
// patří-li jiné firmě s přístupem, aplikace na ni přepne.
import { auditNoValue, isCompanyAccessible, isMyOpenAudit, saveCurrentCompanyId, showToast, state } from './state.js';
import { resetViewState } from './chrome.js';
import { auditCorrectiveSeqMap, correctiveCodeOf, correctiveCodeQuery } from '../views/corrective.js';
import { render } from '../main-render.js';
import { db } from './boot.js';
import { effectiveAuditTab } from '../views/audit-runner.js';

var BASE = (import.meta.env && import.meta.env.BASE_URL) || '/';

var SETTINGS_SLUGS = {
  profile:'profil', appearance:'vzhled', dashboardSettings:'prehled',
  notificationSettings:'notifikace', calendarSync:'notifikace',
  branding:'pracoviste', questions:'druhy-auditu', scheduleManage:'plany',
  auditorGroups:'skupiny-auditoru', companies:'firmy',
  auditRules:'pravidla-auditu', scoreSettings:'pravidla-auditu', scheduleStartSettings:'pravidla-auditu',
  users:'uzivatele', companyAccess:'pristup-k-firmam',
  feedbackNew:'nahlasit-chybu', myFeedback:'moje-podnety', feedbackAdmin:'podnety', contactOperator:'kontakt'
};
var SLUG_VIEWS = {};
Object.keys(SETTINGS_SLUGS).forEach(function(v){ if(!SLUG_VIEWS[SETTINGS_SLUGS[v]]) SLUG_VIEWS[SETTINGS_SLUGS[v]] = v; });

// ---------- stav → adresa ----------
function auditRef(id){
  var a = state.audits.get(id);
  return (a && a.auditNumber!=null) ? auditNoValue(a) : id;
}
function correctiveRef(key){
  var p = String(key).split('::');
  var code = correctiveCodeOf(state.audits.get(p[0]), p[1], p[2]||null);
  return code || ('k/' + encodeURIComponent(key));
}
function pathFromState(){
  switch(state.view){
    case 'dashboard': return '';
    case 'stats': return 'statistiky';
    case 'workplace': return state.workplaceDetailId ? 'pracoviste/'+enc(state.workplaceDetailId) : '';
    case 'history':
      if(state.historyShowTrash) return 'historie/kos';
      return state.historyDetailId ? 'historie/'+enc(auditRef(state.historyDetailId)) : 'historie';
    case 'corrective':
      if(state.correctiveDetailId) return 'opatreni/'+enc(correctiveRef(state.correctiveDetailId));
      if(state.correctiveAuditFilter) return 'opatreni/audit/'+enc(auditRef(state.correctiveAuditFilter));
      return 'opatreni';
    case 'schedule': return 'plan';
    case 'audit':
      if(state.activeAuditId) return 'audit/'+enc(state.activeAuditId);
      return effectiveAuditTab()==='start' ? 'audit/zahajit' : 'audit';
    case 'settings': return 'nastaveni';
    case 'questions':
      return 'nastaveni/druhy-auditu' + (state.questionsEditTypeId ? '/'+enc(state.questionsEditTypeId) : '');
    case 'scheduleManage':
      return 'nastaveni/plany' + (state.editScheduleId ? '/'+(state.editScheduleId==='new' ? 'novy' : enc(state.editScheduleId)) : '');
  }
  return SETTINGS_SLUGS[state.view] ? 'nastaveni/'+SETTINGS_SLUGS[state.view] : '';
}
function enc(s){ return encodeURIComponent(s).replace(/%2F/gi, '/'); }

// ---------- adresa → stav ----------
// Detaily, které potřebují načtená data (audity, plány…), čekají
// v `pending` a vyřídí se před nejbližším překreslením, kdy data jsou.
var pending = null;

function relPath(){
  var p = location.pathname;
  if(p.indexOf(BASE)===0) p = p.slice(BASE.length);
  else if(p.charAt(0)==='/') p = p.slice(1);
  return p.replace(/\/+$/, '');
}
function applyPath(path){
  var segs = path ? path.split('/').map(function(s){ try{ return decodeURIComponent(s); }catch(e){ return s; } }) : [];
  var head = segs[0] || '';
  pending = null;
  if(head==='historie'){
    resetViewState('history');
    if(segs[1]==='kos' && segs.length===2) state.historyShowTrash = true;
    else if(segs[1]) pending = {kind:'history', ref:segs.slice(1).join('/')};
  } else if(head==='opatreni'){
    resetViewState('corrective');
    if(segs[1]==='audit' && segs[2]) pending = {kind:'correctiveAudit', ref:segs.slice(2).join('/')};
    else if(segs[1]==='k' && segs[2]) pending = {kind:'corrective', ref:segs.slice(2).join('/')};
    else if(segs[1]) pending = {kind:'corrective', ref:segs.slice(1).join('/')};
  } else if(head==='plan'){
    resetViewState('schedule');
  } else if((head==='pracoviste' || head==='p') && segs[1]){
    // /p/<id> = krátká stálá adresa z QR štítku, /pracoviste/<id> = stránka pracoviště
    resetViewState('workplace');
    state.workplaceDetailId = null;
    pending = {kind:'workplace', ref:segs[1]};
  } else if(head==='statistiky'){
    resetViewState('stats');
  } else if(head==='audit'){
    resetViewState('audit');
    var leaveRunner = !segs[1] || segs[1]==='zahajit';
    if(leaveRunner){
      state.auditTab = segs[1]==='zahajit' ? 'start' : 'open';
      if(state.activeAuditId){ state.activeAuditId = null; state.draftAudit = null; state.auditParticipantsEditing = false; }
    } else pending = {kind:'audit', ref:segs[1]};
  } else if(head==='nastaveni'){
    var view = segs[1] ? SLUG_VIEWS[segs[1]] : 'settings';
    resetViewState(view || 'settings');
    if(view==='questions'){
      state.questionsEditTypeId = null;
      if(segs[2]) pending = {kind:'questions', ref:segs[2]};
    } else if(view==='scheduleManage' && segs[2]){
      pending = {kind:'schedule', ref:segs[2]};
    }
  } else {
    resetViewState('dashboard');
  }
}

// Audit podle čísla (BOZP-12, 12) nebo id. Přednost má aktivní firma.
function findAuditByRef(ref){
  if(state.audits.has(ref)) return ref;
  var norm = String(ref).toUpperCase(), inCurrent = null, other = null;
  state.audits.forEach(function(a, id){
    if(inCurrent || a.status!=='closed' || a.deletedAt || a.auditNumber==null) return;
    if(auditNoValue(a).toUpperCase()!==norm) return;
    if(!state.currentCompanyId || (a.companyId||'')===state.currentCompanyId) inCurrent = id;
    else if(!other && (!a.companyId || isCompanyAccessible(a.companyId))) other = id;
  });
  return inCurrent || other;
}
// Opatření podle ID (OP-BOZP-12-1) nebo klíče (auditId::otázka[::podotázka]).
function findCorrectiveByRef(ref){
  if(ref.indexOf('::')>=0) return state.audits.has(ref.split('::')[0]) ? ref : null;
  var cq = correctiveCodeQuery(ref);
  var m = cq && /^OP-(.+)-(\d+)$/.exec(cq);
  if(!m) return null;
  var auditId = findAuditByRef(m[1]);
  if(!auditId) return null;
  var map = auditCorrectiveSeqMap(state.audits.get(auditId)), seq = parseInt(m[2], 10), found = null;
  Object.keys(map).forEach(function(k){ if(map[k]===seq) found = auditId+'::'+k; });
  return found;
}
// Detail z jiné firmy (s přístupem) → přepnout na ni, ať je vidět v seznamech.
function switchToCompanyOf(auditId){
  var a = state.audits.get(auditId);
  if(!a || !a.companyId || a.companyId===state.currentCompanyId || !isCompanyAccessible(a.companyId)) return;
  state.currentCompanyId = a.companyId;
  saveCurrentCompanyId(a.companyId);
  state.placeSearch = {};
  var co = state.companies.get(a.companyId);
  showToast('Přepnuto na firmu ' + ((co && co.name) || ''));
}
function dataReadyFor(kind){
  if(!state.myId || !state.rolesReady || !state.ready.companies || !state.ready.audits) return false;
  if(kind==='questions') return state.ready.auditTypes;
  if(kind==='schedule') return state.ready.schedules;
  if(kind==='workplace') return state.ready.workplaces;
  return true;
}
// Audity se načítají jen pro aktivní firmu (ensureAuditsScope). Není-li audit
// z adresy mezi nimi, zeptá se databáze, které firmě patří; má-li k ní uživatel
// přístup, přepne na ni a po načtení jejích auditů zkusí adresu znovu (jednou).
function auditRefOf(p){
  if(p.kind!=='corrective') return p.ref;
  if(p.ref.indexOf('::')>=0) return p.ref.split('::')[0];
  var cq = correctiveCodeQuery(p.ref), m = cq && /^OP-(.+)-(\d+)$/.exec(cq);
  return m ? m[1] : null;
}
function lookupElsewhere(p, notFound){
  var ref = auditRefOf(p);
  if(p.remote || !ref || !db || !db.findAuditCompany){ showToast(notFound); return; }
  db.findAuditCompany(ref).then(function(ids){
    var target = null;
    (ids || []).forEach(function(cid){ if(!target && cid && cid!==state.currentCompanyId && isCompanyAccessible(cid)) target = cid; });
    if(!target){ showToast(notFound); return; }
    state.currentCompanyId = target;
    saveCurrentCompanyId(target);
    state.placeSearch = {};
    var co = state.companies.get(target);
    showToast('Přepnuto na firmu ' + ((co && co.name) || ''));
    pending = {kind:p.kind, ref:p.ref, remote:true};
    render();
  }, function(){ showToast(notFound); });
}
function resolvePending(){
  if(!pending || !dataReadyFor(pending.kind)) return;
  var p = pending, id;
  pending = null;
  if(p.kind==='history'){
    id = findAuditByRef(p.ref);
    if(id){ switchToCompanyOf(id); state.historyDetailId = id; }
    else lookupElsewhere(p, 'Audit ' + p.ref + ' nebyl nalezen.');
  } else if(p.kind==='correctiveAudit'){
    id = findAuditByRef(p.ref);
    if(id){ switchToCompanyOf(id); state.correctiveAuditFilter = id; state.correctiveFilterStatus = ''; }
    else lookupElsewhere(p, 'Audit ' + p.ref + ' nebyl nalezen.');
  } else if(p.kind==='corrective'){
    var key = findCorrectiveByRef(p.ref);
    if(key){ switchToCompanyOf(key.split('::')[0]); state.correctiveDetailId = key; }
    else lookupElsewhere(p, 'Opatření ' + p.ref + ' nebylo nalezeno.');
  } else if(p.kind==='audit'){
    var a = state.audits.get(p.ref);
    if(a && a.status==='closed' && !a.deletedAt){
      // Mezitím uzavřený audit → jeho záznam v Historii.
      resetViewState('history');
      state.historyDetailId = p.ref;
    } else if(a && a.status==='open' && isMyOpenAudit(a)){
      if(state.activeAuditId!==p.ref) App.resumeAudit(p.ref);
    } else {
      state.activeAuditId = null; state.draftAudit = null;
      showToast(a ? 'V cizím rozpracovaném auditu nelze pokračovat.' : 'Rozpracovaný audit nebyl nalezen.');
    }
  } else if(p.kind==='workplace'){
    var wp = state.workplaces.get(p.ref);
    if(wp){
      if(wp.companyId && wp.companyId!==state.currentCompanyId && isCompanyAccessible(wp.companyId)){
        state.currentCompanyId = wp.companyId; saveCurrentCompanyId(wp.companyId); state.placeSearch = {};
      }
      state.workplaceDetailId = p.ref;
    } else {
      resetViewState('dashboard');
      showToast('Pracoviště nebylo nalezeno.');
    }
  } else if(p.kind==='questions'){
    if(state.auditTypes.has(p.ref)) state.questionsEditTypeId = p.ref;
  } else if(p.kind==='schedule'){
    if(p.ref==='novy') App.startNewSchedule();
    else if(state.schedules.has(p.ref)) App.editSchedule(p.ref);
  }
}

// ---------- historie prohlížeče ----------
// paths[i] = adresa i-tého záznamu této relace (index je v history.state.i),
// podle ní se pozná návrat na předchozí záznam.
var paths = [], cur = 0, started = false, ignorePop = 0, replaceNext = false, inPop = false;
try { history.scrollRestoration = 'manual'; } catch(e){}

function fullUrl(path){ return BASE + path; }
function entryState(i){ return {bozpRoute:true, i:i, y:window.scrollY||0}; }

// Před překreslením: při prvním výchozí obrazovka z adresy (až teď — při
// načítání modulů ještě nemusí být stav připravený), pak vyřídit odložený
// detail z adresy.
var initialApplied = false;
function routerBeforeRender(){
  if(!initialApplied){ initialApplied = true; applyPath(relPath()); }
  if(pending) resolvePending();
}

// Po překreslení: promítnout stav do adresy.
function routerAfterRender(){
  if(!state.myId) return;          // přihlášení — adresu (i #token) neměnit
  if(pending || inPop) return;     // detail z adresy čeká na data / Zpět v prohlížeči
  var path = pathFromState();
  if(!started){
    started = true;
    var hs = history.state;
    cur = (hs && hs.bozpRoute && typeof hs.i==='number') ? hs.i : 0;
    paths = []; paths[cur] = path;
    var keepQuery = path===relPath() ? location.search + location.hash : '';
    history.replaceState(entryState(cur), '', fullUrl(path) + keepQuery);
    return;
  }
  if(path===paths[cur]) return;
  if(replaceNext){
    replaceNext = false;
    paths[cur] = path;
    history.replaceState(entryState(cur), '', fullUrl(path));
    return;
  }
  if(cur>0 && paths[cur-1]===path){
    // „← Zpět“ v aplikaci = krok zpět i v historii prohlížeče.
    cur--;
    ignorePop++;
    history.back();
    return;
  }
  history.replaceState(entryState(cur), '', location.href); // uložit polohu posunu
  cur++;
  paths.length = cur;
  paths.push(path);
  history.pushState(entryState(cur), '', fullUrl(path));
}

window.addEventListener('popstate', function(e){
  if(ignorePop>0){ ignorePop--; return; }
  if(!started) return;
  var hs = e.state;
  cur = (hs && hs.bozpRoute && typeof hs.i==='number') ? hs.i : 0;
  var overlay = document.getElementById('overlayRoot');
  if(overlay) overlay.innerHTML = '';
  applyPath(relPath());
  inPop = true;
  try { render(); } finally { inPop = false; }
  if(pending){ paths[cur] = relPath(); return; }
  paths[cur] = pathFromState();
  if(relPath()!==paths[cur]) history.replaceState(entryState(cur), '', fullUrl(paths[cur]));
  if(hs && typeof hs.y==='number') window.scrollTo(0, hs.y);
});

// Zpět z obrazovky (tlačítka „← Zpět“): krok zpět v historii, pokud
// předchozí záznam patří této relaci; jinak vrátí false (volající přejde
// na výchozí obrazovku a ta nahradí aktuální záznam).
function routerBack(){
  if(started && cur>0 && paths[cur-1]!=null){ history.back(); return true; }
  replaceNext = started;
  return false;
}

// Otevřít adresu aplikace (bez obnovení stránky) — „Otevřít místo“ u podnětu.
function openPath(path){
  applyPath(String(path||'').replace(/^\/+|\/+$/g, ''));
  render();
  window.scrollTo(0, 0);
}

export {
  BASE,
  openPath,
  pathFromState,
  routerAfterRender,
  routerBack,
  routerBeforeRender
};
